import React, { useMemo, useState } from 'react';
import { DollarSign, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { db } from '../firebase';
import { collection, doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';

const PayrollView = ({ personal, asistencias, isAdminOrGerente }) => {
    const currentDay = new Date().getDate();
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const isPeriodo1 = currentDay <= 15;

    const [bonosEditables, setBonosEditables] = useState({});
    const [isPaying, setIsPaying] = useState(false);

    const nominaCalculada = useMemo(() => {
        return (personal || []).map(p => {
            const sueldoBase = p.salario || 0;
            const bonoAprobado = bonosEditables[p.id] !== undefined ? Number(bonosEditables[p.id]) : (p.bono || 10);

            // Calcular días programados hasta hoy en el mes
            let scheduledCount = 0;
            for (let d = 1; d <= currentDay; d++) {
                const dateObj = new Date(currentYear, currentMonth, d);
                const dayOfWeek = dateObj.getDay();
                const hm = p.horario_maestro?.[dayOfWeek];
                if (hm && hm.activo) {
                    scheduledCount++;
                }
            }

            // Filtrar asistencias del mes
            const logsMes = (asistencias || []).filter(a => {
                if (!a.fecha_texto) return false;
                const aDate = new Date(a.fecha_texto + 'T12:00:00Z');
                return aDate.getMonth() === currentMonth && aDate.getFullYear() === currentYear && a.email === p.email;
            });

            const presentCount = logsMes.filter(a => a.cumplido).length;
            const logsIncompletos = logsMes.filter(a => !a.cumplido).length;

            // Condición del bono: 100% asistencias cumplidas
            const hasFalta = logsIncompletos > 0 || presentCount < scheduledCount;
            const enablesBonus = !hasFalta;

            // Cálculo del pago de la próxima quincena
            let montoQuincena = 0;
            if (isPeriodo1) {
                montoQuincena = sueldoBase / 2;
            } else {
                montoQuincena = (sueldoBase / 2) + (enablesBonus ? bonoAprobado : 0);
            }

            return {
                id: p.id,
                nombre: p.name || p.nombre,
                rol: p.cargo || p.rol,
                departamento: p.departamento || p.cargo || p.rol,
                sueldoBase,
                bonoAprobado,
                scheduledCount,
                presentCount,
                hasFalta,
                enablesBonus,
                montoQuincena
            };
        });
    }, [personal, asistencias, currentDay, currentMonth, currentYear, isPeriodo1, bonosEditables]);

    const handlePagarNomina = async () => {
        if (nominaCalculada.length === 0) return;
        
        const totalNomina = nominaCalculada.reduce((acc, curr) => acc + curr.montoQuincena, 0);
        
        const { isConfirmed } = await Swal.fire({
            title: 'Confirmar Pago de Nómina',
            text: `¿Estás seguro de registrar el pago de nómina por un total de $${totalNomina.toFixed(2)}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, Pagar',
            cancelButtonText: 'Cancelar',
            background: '#1e293b',
            color: '#fff'
        });
        
        if (!isConfirmed) return;
        
        setIsPaying(true);
        try {
            const batch = writeBatch(db);
            
            nominaCalculada.forEach(n => {
                const pRef = doc(db, 'personal', n.id);
                batch.update(pRef, { 
                    estatus_pago: 'Pagado', 
                    ultimo_monto_pagado: n.montoQuincena,
                    fecha_ultimo_pago: serverTimestamp() 
                });
            });
            
            const payload = {
                tipo: 'gasto',
                categoria: 'Personal',
                descripcion: `Pago Nómina Quincenal (${isPeriodo1 ? 'P1' : 'P2'})`,
                concepto: `Pago Nómina Quincenal (${isPeriodo1 ? 'P1' : 'P2'})`,
                monto: totalNomina,
                amount: totalNomina,
                fecha: new Date().toISOString().split('T')[0],
                registrado_por: 'Admin Nómina',
                createdAt: serverTimestamp()
            };
            
            const gastoRef1 = doc(collection(db, 'finanzas_gastos'));
            const gastoRef2 = doc(collection(db, 'finanzas_movimientos'));
            
            batch.set(gastoRef1, payload);
            batch.set(gastoRef2, payload);
            
            await batch.commit();
            Swal.fire('¡Pagado!', 'Nómina registrada exitosamente.', 'success');
        } catch (err) {
            console.error("Error al pagar nómina:", err);
            Swal.fire('Error', 'No se pudo registrar el pago.', 'error');
        } finally {
            setIsPaying(false);
        }
    };

    if (!isAdminOrGerente) {
        return (
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-xl">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <DollarSign size={20} className="text-amber-500" /> Control de Asistencia y Cumplimiento
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-300">
                        <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">Miembro</th>
                                <th className="px-6 py-4">Departamento</th>
                                <th className="px-6 py-4 text-center">Días Trabajados</th>
                                <th className="px-6 py-4 text-center">Estado del Bono</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {nominaCalculada.map((n) => (
                                <tr key={n.id} className="hover:bg-slate-700/30">
                                    <td className="px-6 py-4 font-bold text-white">{n.nombre}</td>
                                    <td className="px-6 py-4 text-slate-400">{n.departamento}</td>
                                    <td className="px-6 py-4 text-center font-bold text-indigo-400">
                                        {n.presentCount} / {n.scheduledCount}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {n.enablesBonus ? (
                                            <span className="text-emerald-400 font-bold text-xs flex items-center justify-center gap-1">
                                                <CheckCircle size={14} /> 100% Cumplido
                                            </span>
                                        ) : (
                                            <span className="text-rose-400 font-bold text-xs flex items-center justify-center gap-1">
                                                <XCircle size={14} /> Bono Perdido
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <DollarSign size={20} className="text-emerald-500" /> Nómina Quincenal
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">
                        Mostrando pagos correspondientes al {isPeriodo1 ? 'Periodo 1 (Día 1 al 15)' : 'Periodo 2 (Día 16 al 30/31)'}
                    </p>
                </div>
                <div className="flex items-center gap-3 self-end sm:self-auto">
                    <span className="bg-indigo-500/20 text-indigo-300 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 border border-indigo-500/30">
                        <Sparkles size={12} /> Bono Editable
                    </span>
                    <button
                        onClick={handlePagarNomina}
                        disabled={isPaying || nominaCalculada.length === 0}
                        className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-xl flex items-center gap-2 transition-colors shadow-lg shadow-emerald-500/20 text-sm"
                    >
                        <DollarSign size={16} /> 
                        {isPaying ? 'Procesando...' : `Pagar Nómina ($${nominaCalculada.reduce((acc, curr) => acc + curr.montoQuincena, 0).toFixed(2)})`}
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-300">
                    <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                        <tr>
                            <th className="px-6 py-4">Miembro</th>
                            <th className="px-6 py-4 text-center">Sueldo Base</th>
                            <th className="px-6 py-4 text-center">Progreso</th>
                            <th className="px-6 py-4 text-center">Asistencia</th>
                            <th className="px-6 py-4 text-center">Monto Bono</th>
                            <th className="px-6 py-4 text-right">Monto a Pagar</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                        {nominaCalculada.map((n) => (
                            <tr key={n.id} className="hover:bg-slate-700/30">
                                <td className="px-6 py-4 font-bold text-white">
                                    {n.nombre}
                                    <span className="block text-[10px] text-slate-500 font-normal">{n.departamento}</span>
                                </td>
                                <td className="px-6 py-4 text-center font-mono">${(n.sueldoBase).toFixed(2)}</td>
                                <td className="px-6 py-4 text-center font-bold text-indigo-400">
                                    {n.presentCount} / {n.scheduledCount} días
                                </td>
                                <td className="px-6 py-4 text-center">
                                    {n.enablesBonus ? (
                                        <span className="bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-full text-xs font-bold border border-emerald-500/20">
                                            Válido
                                        </span>
                                    ) : (
                                        <span className="bg-rose-500/10 text-rose-400 px-2 py-1 rounded-full text-xs font-bold border border-rose-500/20">
                                            Falta
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-center">
                                    {n.enablesBonus ? (
                                        <select 
                                            value={bonosEditables[n.id] !== undefined ? bonosEditables[n.id] : (n.bonoAprobado || 10)}
                                            onChange={(e) => setBonosEditables({ ...bonosEditables, [n.id]: Number(e.target.value) })}
                                            className="bg-slate-900 border border-slate-700 text-emerald-400 font-mono font-bold rounded px-2 py-1 focus:outline-none"
                                        >
                                            <option value={10}>$10.00</option>
                                            <option value={20}>$20.00</option>
                                        </select>
                                    ) : (
                                        <span className="font-mono text-slate-500">$0.00</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-right font-mono font-bold text-white text-base">
                                    ${n.montoQuincena.toFixed(2)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PayrollView;
